// Shared primitives: logo, CTAs, section heads, window chrome, the silent loop.
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { CRWIcon, type IconName } from './icons';
import { useSettled } from './hooks';
import { BRAND } from '../lib/site';

// The lockup: the switch mark and the lowercase name, one outlined SVG (built in 06_SITE/brand/).
export function Logo({ size = 26 }: { size?: number }) {
  const h = Math.round(size * 0.82);
  return <img src="/logo.svg" width={Math.round(h * 7)} height={h} alt={BRAND} style={{ display: 'block' }} />;
}

interface BtnProps {
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  busy?: boolean;
  type?: 'button' | 'submit';
}

// Premium white CTA (Linear/Raycast style).
export function GradBtn({ children, size = 'md', onClick, style, disabled, busy, type = 'button' }: BtnProps) {
  const pad = size === 'lg' ? '14px 28px' : size === 'sm' ? '8px 16px' : '11px 22px';
  const fs = size === 'lg' ? 16 : size === 'sm' ? 13 : 14;
  const off = disabled || busy;
  return (
    <button type={type} className="crw-gradbtn" onClick={onClick} disabled={off} aria-busy={busy || undefined} style={{ ...(off ? { opacity: 0.55, cursor: 'not-allowed' } : null), padding: pad, fontSize: fs, ...style }}>
      {children}
    </button>
  );
}

// Metal morphism CTA: rotating chrome border, dark glass core. `neutral`
// renders the silver cut for chrome placements like the nav.
export function MetalBtn({ children, size = 'md', onClick, style, disabled, busy, neutral, type = 'button' }: BtnProps & { neutral?: boolean }) {
  const pad = size === 'lg' ? '14px 30px' : size === 'sm' ? '8px 18px' : '12px 24px';
  const fs = size === 'lg' ? 15.5 : size === 'sm' ? 13 : 14;
  const off = disabled || busy;
  return (
    <button type={type} className={`crw-metal${neutral ? ' crw-metal-neutral' : ''}`} onClick={onClick} disabled={off} aria-busy={busy || undefined} style={{ ...(off ? { opacity: 0.55, cursor: 'not-allowed' } : null), ...style }}>
      <span style={{ padding: pad, fontSize: fs }}>{children}</span>
    </button>
  );
}

export function SectionHead({ label, title, sub, center, maxW = 620 }: { label?: string; title: ReactNode; sub?: ReactNode; center?: boolean; maxW?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: center ? 'center' : 'flex-start', textAlign: center ? 'center' : 'left', marginBottom: 44 }}>
      {label ? <span className="crw-eyebrow"><i></i>{label}</span> : null}
      <h2 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.08, textWrap: 'balance' }}>{title}</h2>
      {sub ? <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.65, color: 'var(--fg-2)', maxWidth: maxW, textWrap: 'pretty' }}>{sub}</p> : null}
    </div>
  );
}

export function MonoNote({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.06em', color: 'var(--fg-3)', textTransform: 'uppercase', ...style }}>{children}</span>;
}

// Neutral icon tile: glass plate, fg-2 stroke icon, no halo.
export function NeutralTile({ d, size = 38 }: { d: IconName; size?: number }) {
  return (
    <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 11, background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.12)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 20px rgba(0,0,0,0.4)', color: 'var(--fg-2)' }}>
      <CRWIcon d={d} size={Math.round(size * 0.46)} />
    </span>
  );
}

// The Premiere Pro tile.
export function PrTile({ size = 22, fs = 10 }: { size?: number; fs?: number }) {
  return (
    <span style={{ width: size, height: size, flex: 'none', borderRadius: size * 0.22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1A1034', border: '1px solid rgba(153,153,255,0.4)', color: '#9999FF', fontWeight: 700, fontSize: fs, fontFamily: 'var(--font-body)', letterSpacing: '-0.02em' }}>Pr</span>
  );
}

// Window chrome for demo scenes: glass chrome inside a gradient ring.
export function PanelFrame({ title, children, badge, pad = 16 }: { title: string; children: ReactNode; badge?: ReactNode; pad?: number }) {
  const settled = useSettled();
  return (
    <div style={{ height: '100%', borderRadius: 16, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04) 30%, rgba(var(--light-soft-rgb),0.25) 70%, rgba(255,255,255,0.06))', boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 90px rgba(var(--light-rgb),0.07)' }}>
      <div data-settled={settled ? 'true' : undefined} style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 15, overflow: 'hidden', background: 'rgba(9,9,12,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 15px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))' }}>
          <img src="/logo-mark.svg" width="19" height="10" alt="" style={{ opacity: 0.75 }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--fg-3)', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          <span style={{ flex: 1 }}></span>
          {badge ? <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--fg-2)', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--line-2)' }}>{badge}</span> : null}
        </div>
        <div style={{ flex: 1, padding: pad, minHeight: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{children}</div>
      </div>
    </div>
  );
}

// A silent excerpt that loops only while it is on screen. `muted` is set on
// the element itself (React does not always reflect the prop as an attribute,
// and mobile browsers refuse to autoplay without it). Under reduced motion the
// poster frame stays and nothing plays.
export function LoopVideo({ src, poster, style, onTime }: { src: string; poster: string; style?: CSSProperties; onTime?: (t: number) => void }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) void el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref} src={src} poster={poster} muted loop playsInline preload="metadata" aria-hidden="true" tabIndex={-1}
      onTimeUpdate={onTime ? (e) => onTime(e.currentTarget.currentTime) : undefined}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', background: '#000', ...style }}
    />
  );
}
